import { isAnyOf } from '@reduxjs/toolkit'
import { enhancedTmsApiService } from '#api/enhancements/enhancedTmsApiService'
import type { EnhancedProductFamilyId, EnhancedProductVariantId } from '#api/enhancements/enhancedTmsApiTypes'
import { createNewConfiguration, type LocalConfigurationId, setView, truckConfigurationSlice } from '#app/store/slices/configurationSlice'
import { type ConfigurationStage, type RequestConfigurationStep, stageForView } from '#app/types/Configuration'
import { makeSerializedError, type SerializedError } from '#features/ErrorHandling/errorHelpers'
import { getAllFamilies } from '#pages/Configuration/utils/collections'
import type { AppMiddleware } from '../configurationRequestMiddleware'

export type StepErrorCause = {
  stepErrorType: 'unknown'
} & SerializedError

export const makeRequiredSystemStep = (viewStep: ConfigurationStage) =>
  viewStep === 'PendingStep' || viewStep === 'GeneralStep' ? 'GeneralStep' : 'DetailedStep'

const matcher = isAnyOf(setView.match, createNewConfiguration.match)

export const configurationStepListener = (middleware: AppMiddleware) => {
  const mostRecentRequest: Partial<Record<LocalConfigurationId, { step: 'DetailedStep' | 'GeneralStep'; time: Timestamp }>> = {}

  middleware.startListening({
    matcher: matcher,
    effect: async function handleConfigurationStepChanges(action, listenerApi) {
      if (!matcher(action)) {
        return
      }

      const { view, id } = setView.match(action) ? action.payload : { view: action.payload.step.view, id: action.payload.id }
      const requiredStage = stageForView(view)
      // in particular when the set view is 'copy', this hits
      if (requiredStage === undefined || !id) {
        return
      }
      if (view === 'configuration/error') {
        return
      }

      const requiredStep = makeRequiredSystemStep(requiredStage)
      const configuration = listenerApi.getState().truckConfigurations.configurations[id]
      if (!configuration?.step || configuration.step.system === requiredStep || !configuration.uuid) {
        return
      }
      // if the middleware was/is already sending the step request, do not do it again (within one second)
      const lastHandle = mostRecentRequest[configuration.id]
      if (lastHandle && lastHandle.step === requiredStep && Date.now() - lastHandle.time < 1_000) {
        return
      }
      mostRecentRequest[configuration.id] = { step: requiredStep, time: Date.now() }

      try {
        // all transitions from GeneralStep to DetailedStep requires an explicit selection of all variants that have been used
        if (configuration.step.system === 'GeneralStep' && requiredStep === 'DetailedStep') {
          const families = getAllFamilies(configuration.productGroups)
          const withSelectables = families.filter((x) => x.variants.length > 1 && x.selectionType !== 'Explicit')
          const selection = withSelectables
            .map((x) => ({ familyId: x.id, variantId: x.variants.find((x) => x.isSelected)?.id }))
            .filter((x) => x.variantId !== undefined)
          if (selection.length > 0) {
            await listenerApi.dispatch(
              enhancedTmsApiService.endpoints.postApiConfigurationByUuidSelectVariants.initiate({
                uuid: configuration.uuid,
                multiVariantSelectionRequest: {
                  variantsForSelection: selection as { familyId: EnhancedProductFamilyId; variantId: EnhancedProductVariantId }[],
                },
              }),
            )
            // mark the selection as explicit, too
            const configurationSelection = { ...configuration.configuredFamilies }
            for (const entry of selection) {
              if (entry.variantId === undefined) {
                continue
              }
              const selectionEntry = configurationSelection[entry.familyId]
              configurationSelection[entry.familyId] = {
                ...selectionEntry,
                isLocked: selectionEntry?.isLocked ?? false,
                printOptions: selectionEntry?.printOptions ?? {},
                selectedVariant: { id: entry.variantId, timestamp: Date.now(), previousSelection: selectionEntry?.selectedVariant?.id },
              }
            }

            listenerApi.dispatch(
              truckConfigurationSlice.actions._placeConfiguration({
                configuration: {
                  ...configuration,
                  configuredFamilies: configurationSelection,
                },
                reason: 'variant-selection',
              }),
            )
          }
        }

        const response = await listenerApi
          .dispatch(
            enhancedTmsApiService.endpoints.postApiConfigurationByUuidStepAndStep.initiate({
              uuid: configuration.uuid,
              step: requiredStep === 'GeneralStep' ? 1 : 2,
              configurationStepDataOptions: {
                returnCurrentConfiguration: true,
              },
            }),
          )
          .unwrap()

        // do not place the step if the loading step is not the one we are currently on
        if (configuration.step.loading && configuration.step.loading.to !== response.currentStep) {
          return
        }

        listenerApi.dispatch(
          truckConfigurationSlice.actions._placeStep({
            id,
            step: {
              system: response.currentStep,
              loading: undefined,
              error: undefined,
            },
          }),
        )
      } catch (e) {
        console.error('Error while fetching configuration step:', e)
        const error = makeSerializedError(e)

        listenerApi.dispatch(
          truckConfigurationSlice.actions._placeStep({
            id,
            step: {
              system: 'unknown',
              requested: requiredStep as RequestConfigurationStep,
              loading: undefined,
              error: error
                ? {
                    stepErrorType: 'unknown',
                    ...error,
                  }
                : undefined,
            },
          }),
        )
      }
    },
  })
}
